import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Upload, FileSpreadsheet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { toast } from "sonner";

// Accept everything SheetJS supports + JSON
const ACCEPT_ALL = ".xlsx,.xls,.xlsm,.xlsb,.csv,.tsv,.txt,.ods,.dif,.prn,.html,.htm,.json";

export default function ExcelUploader({
  onParsed,
  accept = ACCEPT_ALL,
  label,
  testid = "excel-uploader",
}) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      let rows = [];

      if (ext === "json") {
        // Plain JSON array of objects
        const text = await file.text();
        const parsed = JSON.parse(text);
        if (!Array.isArray(parsed)) throw new Error("JSON must be an array of objects");
        rows = parsed;
      } else {
        // Everything else through SheetJS (handles xlsx/xls/xlsm/csv/tsv/ods/dif/html/etc.)
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      }

      if (!rows.length) {
        toast.error(t("common.file_empty"));
        return;
      }
      await onParsed(rows, file.name);
    } catch (err) {
      console.error(err);
      toast.error(t("common.file_read_error", { reason: err.message || "unknown format" }));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handle}
        data-testid={`${testid}-input`}
      />
      <Button
        variant="outline"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="gap-2"
        data-testid={`${testid}-button`}
        title="xlsx · xls · csv · tsv · ods · json · html"
      >
        {busy ? <FileSpreadsheet className="animate-pulse" size={16} /> : <Upload size={16} />}
        {busy ? t("common.processing") : (label || t("common.upload_excel"))}
      </Button>
    </>
  );
}
